import { useEffect, useState } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  ComposedChart,
  ReferenceLine,
  BarChart,
  Cell,
} from 'recharts';
import { api } from '@/lib/api';
import { useMonthStore } from '@/lib/monthStore';
import { usePeriodStore, monthIdsQuery } from '@/lib/periodStore';
import { GlassCard } from '@/components/ui/GlassCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatCurrency, formatCurrencyCompact, formatPercent } from '@/lib/format';
import { cn } from '@/lib/cn';
import type {
  DailyCashSummary,
  MonthPnL,
  BreakEvenResult,
  FinanceResponse,
  ExcludedMonth,
} from '@/types';

const MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const TOOLTIP_STYLE = {
  background: '#0a0a0a',
  border: '2px solid #ff8a65',
  borderRadius: 0,
  boxShadow: '4px 4px 0 0 #000000',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '12px',
  color: '#f5f5f5',
};

export default function Finance() {
  const selectedMonthId = useMonthStore((s) => s.selectedMonthId);
  const monthIds = usePeriodStore((s) => s.monthIds);
  const isMultiMonth = monthIds.length > 1;

  const [cashSummary, setCashSummary] = useState<DailyCashSummary[]>([]);
  const [pnl, setPnl] = useState<MonthPnL | null>(null);
  const [breakEven, setBreakEven] = useState<BreakEvenResult | null>(null);
  const [financeAgg, setFinanceAgg] = useState<FinanceResponse | null>(null);
  const [excludedMonths, setExcludedMonths] = useState<ExcludedMonth[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (monthIds.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setExcludedMonths([]);

    if (isMultiMonth) {
      const q = monthIdsQuery(monthIds);
      api
        .get<FinanceResponse>(`/aggregate/finance?monthIds=${q}`)
        .then((res) => {
          setFinanceAgg(res);
          setExcludedMonths(res.excluded);
          setPnl(res.finance.pnl);
          setBreakEven(res.finance.breakEven);
          setCashSummary([]);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      const id = selectedMonthId || monthIds[0];
      if (!id) {
        setLoading(false);
        return;
      }
      Promise.all([
        api.get<DailyCashSummary[]>(`/finance/month/${id}/cash-summary`),
        api.get<MonthPnL>(`/finance/month/${id}/pnl`),
        api.get<BreakEvenResult>(`/finance/month/${id}/break-even`),
      ])
        .then(([cash, p, be]) => {
          setCashSummary(cash);
          setPnl(p);
          setBreakEven(be);
          setFinanceAgg(null);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [selectedMonthId, monthIds, isMultiMonth]);

  if (monthIds.length === 0) {
    return (
      <>
        <PageHeader tag="// MÓDULO :: 02" title="Finanzas" subtitle="Caja, P&L y Break-Even" />
        <EmptyState
          icon="account_balance"
          title="Sin mes seleccionado"
          description="Carga un archivo Excel para ver las métricas financieras."
        />
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="material-symbols-outlined text-5xl text-primary animate-spin">
          progress_activity
        </span>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        tag="// MÓDULO :: 02 / FINANZAS"
        title="Control Financiero"
        subtitle={
          isMultiMonth
            ? `Vista multi-mes · ${monthIds.length} meses · P&L acumulado, BEP prorrateado, canales`
            : 'Caja diaria, P&L mensual y Break-Even'
        }
      />

      {/* Banner de meses excluidos */}
      {excludedMonths.length > 0 && (
        <div className="mb-6 bg-surface-container border-2 border-warning shadow-[3px_3px_0_0_#000000] p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-warning mt-0.5">warning</span>
          <div className="flex-1">
            <p className="font-bold text-text-bright font-mono uppercase tracking-widest text-sm">
              ⚠ {excludedMonths.length} mes(es) excluido(s) por baja calidad
            </p>
            <ul className="text-text-muted mt-2 text-xs space-y-1 list-disc list-inside font-mono">
              {excludedMonths.map((m) => (
                <li key={m.monthId}>
                  {m.fileName} — {m.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* P&L Summary */}
      {pnl && (
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
          <KpiBox
            label={isMultiMonth ? 'Ingresos NSR Total' : 'Ingresos NSR'}
            value={formatCurrency(pnl.nsr)}
            icon="trending_up"
            color="tertiary"
          />
          <KpiBox
            label={isMultiMonth ? 'COGS Total' : 'Costo COGS'}
            value={formatCurrency(pnl.cogs)}
            icon="shopping_cart"
            color="warning"
          />
          <KpiBox
            label={isMultiMonth ? 'Utilidad Bruta Total' : 'Utilidad Bruta'}
            value={formatCurrency(pnl.grossProfit)}
            icon="savings"
            color="primary"
          />
          <KpiBox
            label={isMultiMonth ? 'NOI Total' : 'NOI Operativo'}
            value={formatCurrency(pnl.noi)}
            icon="account_balance"
            color={pnl.noi > 0 ? 'tertiary' : 'error'}
            sub={formatPercent(pnl.noiMargin)}
          />
        </section>
      )}

      {/* === VISTA MULTI-MES === */}
      {isMultiMonth && financeAgg && (
        <>
          <div className="grid grid-cols-12 gap-5 mb-6">
            {/* P&L por mes */}
            <GlassCard
              variant="kinetic"
              padding="md"
              rounded="none"
              className="col-span-12 lg:col-span-8"
            >
              <div className="mb-4 pb-3 border-b-2 border-outline">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                  // P&L_TIMELINE
                </p>
                <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                  Tendencia de Ganancia (NOI)
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart
                  data={financeAgg.finance.pnlByMonth.map((m) => ({
                    label: `${MONTH_NAMES[m.month - 1]} ${String(m.year).slice(-2)}`,
                    nsr: m.nsr,
                    noi: m.noi,
                    margin: m.noiMargin,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis
                    dataKey="label"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                    tickFormatter={(v) => formatCurrencyCompact(v)}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                    tickFormatter={(v) => `${v.toFixed(0)}%`}
                  />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <ReferenceLine y={0} yAxisId="left" stroke="#525252" />
                  <Bar
                    yAxisId="left"
                    dataKey="nsr"
                    fill="#22d3ee"
                    stroke="#000000"
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="noi"
                    stroke="#ff8a65"
                    strokeWidth={3}
                    dot={{ r: 5, fill: '#ff8a65', stroke: '#000000', strokeWidth: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </GlassCard>

            {/* Break-Even */}
            {breakEven && (
              <GlassCard
                variant="glow"
                padding="md"
                rounded="none"
                className="col-span-12 lg:col-span-4"
              >
                <div className="mb-4 pb-3 border-b-2 border-primary">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
                    // BEP_AGREGADO
                  </p>
                  <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                    Punto de Equilibrio
                  </h3>
                </div>
                <div className="space-y-4">
                  <div className="bg-surface border-2 border-primary p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
                      BEP UNIDADES
                    </p>
                    <p className="font-condensed text-4xl text-text-bright num leading-none">
                      {formatCurrency(breakEven.breakEvenUnits).replace('$', '')}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                      empanadas en {breakEven.monthsCovered}m
                    </p>
                  </div>
                  <div className="bg-surface border-2 border-secondary p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-1">
                      BEP REVENUE
                    </p>
                    <p className="font-condensed text-3xl text-text-bright num leading-none">
                      {formatCurrency(breakEven.breakEvenRevenue)}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                      ingresos mínimos
                    </p>
                  </div>
                  <div className="bg-surface border-2 border-outline p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
                      SAFETY MARGIN
                    </p>
                    <p
                      className={cn(
                        'font-condensed text-3xl num leading-none',
                        breakEven.safetyMargin > 0 ? 'text-tertiary' : 'text-error',
                      )}
                    >
                      {formatPercent(breakEven.safetyMargin)}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                      {breakEven.actualRevenue > breakEven.breakEvenRevenue
                        ? `+${formatCurrency(breakEven.actualRevenue - breakEven.breakEvenRevenue)} sobre BEP`
                        : `${formatCurrency(breakEven.breakEvenRevenue - breakEven.actualRevenue)} bajo BEP`}
                    </p>
                  </div>
                </div>
              </GlassCard>
            )}
          </div>

          {/* Fila 2: Cash summary + Canales */}
          <div className="grid grid-cols-12 gap-5 mb-6">
            <GlassCard
              variant="kinetic"
              padding="md"
              rounded="none"
              className="col-span-12 lg:col-span-7"
            >
              <div className="mb-4 pb-3 border-b-2 border-outline">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                  // CASH_SUMMARY_AGREGADO
                </p>
                <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                  Cuadre de Caja Acumulado
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <CashBox label="OK" value={financeAgg.finance.cashSummary.daysOk} variant="tertiary" />
                <CashBox label="WARN" value={financeAgg.finance.cashSummary.daysWarn} variant="warning" />
                <CashBox
                  label="ALTA VAR"
                  value={financeAgg.finance.cashSummary.daysHighVariance}
                  variant="error"
                />
                <CashBox
                  label="ACUM"
                  value={financeAgg.finance.cashSummary.totalCumulativeVariance}
                  variant="primary"
                  isCurrency
                />
              </div>
            </GlassCard>

            <GlassCard
              variant="kinetic"
              padding="md"
              rounded="none"
              className="col-span-12 lg:col-span-5"
            >
              <div className="mb-4 pb-3 border-b-2 border-outline">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                  // CHANNELS_HORIZONTAL
                </p>
                <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                  Mix de Canales
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={financeAgg.finance.paymentChannels.map((c) => ({
                    name: c.channel,
                    total: c.total,
                    pct: c.participation,
                  }))}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis
                    type="number"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                    tickFormatter={(v) => formatCurrencyCompact(v)}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                    width={70}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v: number) => [formatCurrency(v), 'Total']}
                  />
                  <Bar dataKey="total" stroke="#000000" strokeWidth={2}>
                    {financeAgg.finance.paymentChannels.map((_ch, idx) => (
                      <Cell
                        key={idx}
                        fill={['#ff8a65', '#22d3ee', '#a3e635', '#fb923c'][Math.min(idx, 3)]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </GlassCard>
          </div>
        </>
      )}

      {/* === VISTA SINGLE-MONTH === */}
      {!isMultiMonth && (
        <>
          <div className="grid grid-cols-12 gap-5 mb-6">
            <GlassCard
              variant="kinetic"
              padding="md"
              rounded="none"
              className="col-span-12 lg:col-span-8"
            >
              <div className="mb-4 pb-3 border-b-2 border-outline">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                  // CASH_DAILY
                </p>
                <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                  Cuadre de Caja Diario
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={cashSummary}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis
                    dataKey="dayOfMonth"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                    tickFormatter={(v) => formatCurrencyCompact(v)}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v: number) => [formatCurrency(v), 'Varianza']}
                    labelFormatter={(label) => `Día ${label}`}
                  />
                  <ReferenceLine y={0} stroke="#525252" />
                  <Bar
                    dataKey="variance"
                    fill="#ff8a65"
                    stroke="#000000"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="cumulativeVariance"
                    stroke="#22d3ee"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </GlassCard>

            {breakEven && (
              <GlassCard
                variant="glow"
                padding="md"
                rounded="none"
                className="col-span-12 lg:col-span-4"
              >
                <div className="mb-4 pb-3 border-b-2 border-primary">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
                    // BEP_SINGLE
                  </p>
                  <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                    Punto de Equilibrio
                  </h3>
                </div>
                <div className="space-y-4">
                  <div className="bg-surface border-2 border-primary p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
                      BEP UNIDADES
                    </p>
                    <p className="font-condensed text-4xl text-text-bright num leading-none">
                      {formatCurrency(breakEven.breakEvenUnits).replace('$', '')}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                      empanadas/mes
                    </p>
                  </div>
                  <div className="bg-surface border-2 border-secondary p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-1">
                      BEP REVENUE
                    </p>
                    <p className="font-condensed text-3xl text-text-bright num leading-none">
                      {formatCurrency(breakEven.breakEvenRevenue)}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                      ingresos mínimos
                    </p>
                  </div>
                  <div className="bg-surface border-2 border-outline p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
                      SAFETY MARGIN
                    </p>
                    <p
                      className={cn(
                        'font-condensed text-3xl num leading-none',
                        breakEven.safetyMargin > 0 ? 'text-tertiary' : 'text-error',
                      )}
                    >
                      {formatPercent(breakEven.safetyMargin)}
                    </p>
                  </div>
                </div>
              </GlassCard>
            )}
          </div>

          <GlassCard variant="kinetic" padding="md" rounded="none" className="overflow-hidden">
            <div className="mb-4 pb-3 border-b-2 border-outline">
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // CASH_TABLE
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Detalle Diario
              </h3>
            </div>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-outline">
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Día
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Esperado
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Declarado
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Varianza
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      %
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Acumulado
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cashSummary.map((d, i) => (
                    <tr
                      key={d.date}
                      className={cn(
                        'border-b border-outline/50',
                        i % 2 === 1 && 'bg-surface-container/30',
                      )}
                    >
                      <td className="px-3 py-2 num font-bold text-text-bright">{d.dayOfMonth}</td>
                      <td className="px-3 py-2 num text-text-bright">
                        {formatCurrency(d.totalCalculado)}
                      </td>
                      <td className="px-3 py-2 num text-text-bright">
                        {formatCurrency(d.totalDeclarado)}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 num font-bold',
                          Math.abs(d.variance) > 50 ? 'text-error' : 'text-text-bright',
                        )}
                      >
                        {formatCurrency(d.variance)}
                      </td>
                      <td className="px-3 py-2 num text-text-muted">
                        {formatPercent(d.variancePercent)}
                      </td>
                      <td className="px-3 py-2 num text-text-bright">
                        {formatCurrency(d.cumulativeVariance)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            'inline-block px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest font-bold border-2 border-black',
                            d.status === 'OK' && 'bg-tertiary text-black',
                            d.status === 'WARN' && 'bg-warning text-black',
                            d.status === 'HIGH_VARIANCE' && 'bg-error text-white',
                          )}
                        >
                          {d.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </>
      )}
    </>
  );
}

interface KpiBoxProps {
  label: string;
  value: string;
  icon: string;
  color: 'primary' | 'tertiary' | 'warning' | 'error';
  sub?: string;
}

function KpiBox({ label, value, icon, color, sub }: KpiBoxProps) {
  const colorMap = {
    primary: 'border-primary text-primary',
    tertiary: 'border-tertiary text-tertiary',
    warning: 'border-warning text-warning',
    error: 'border-error text-error',
  };
  return (
    <div
      className={cn(
        'bg-surface border-2 p-5 shadow-[4px_4px_0_0_#000000]',
        colorMap[color],
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="material-symbols-outlined text-3xl">{icon}</span>
        <span
          className={cn(
            'w-2 h-2 border-2 border-black',
            color === 'primary' && 'bg-primary',
            color === 'tertiary' && 'bg-tertiary',
            color === 'warning' && 'bg-warning',
            color === 'error' && 'bg-error',
          )}
        />
      </div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
        {label}
      </p>
      <p className="font-condensed text-3xl text-text-bright num leading-none">{value}</p>
      {sub && (
        <p className="font-mono text-xs uppercase tracking-widest text-text-muted mt-2 num">
          {sub}
        </p>
      )}
    </div>
  );
}

function CashBox({
  label,
  value,
  variant,
  isCurrency,
}: {
  label: string;
  value: number;
  variant: 'primary' | 'tertiary' | 'warning' | 'error';
  isCurrency?: boolean;
}) {
  const colorMap = {
    primary: 'border-primary text-primary',
    tertiary: 'border-tertiary text-tertiary',
    warning: 'border-warning text-warning',
    error: 'border-error text-error',
  };
  return (
    <div className={cn('bg-surface border-2 p-3', colorMap[variant])}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
        {label}
      </p>
      <p
        className={cn(
          'font-condensed text-2xl num leading-none',
          variant === 'tertiary' && 'text-tertiary',
          variant === 'warning' && 'text-warning',
          variant === 'error' && 'text-error',
          variant === 'primary' && 'text-primary',
        )}
      >
        {isCurrency ? formatCurrency(value) : value}
      </p>
    </div>
  );
}
